import * as React from 'react';

import { Step, StepContent, StepLabel, Stepper } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Alert, Grid, Spacer, Typography } from '@emburse/ui-core';

import { signupForEmburseCards } from '@ReactComponents/../utils/emburseCardsSignupHelper';
import { getPartnerAppLogoPath, getPartnerSignupLogoStyle } from '@ReactComponents/Branding/partnerBranding';
import { GoToEmburse } from '@ReactComponents/GoToEmburse';
import { CompanyInformationForm, ICompanyForm } from '@ReactComponents/Onboard/CompanyInformation';

import { ShowModal } from '@Actions/index';
import { useTimeoutManager } from '@ReactHooks/useTimeoutManager';
import { IAppStateContainer } from '@Reducers/index';

import { CompanyApi } from '@ClientApi/company';
import { GoogleAnalyticsService, GoogleEvents } from '@ClientApi/google';
import { PendoEvents, PendoService } from '@ClientApi/pendo';
import { PARTNER_ID_RSM_FAO, PARTNER_ID_RSM_VAR, PARTNER_ID_WTI_GLOBAL } from '@Shared/constants/partners';
import { ICompanyModel } from '@Shared/models';

import { SignUpConfirmationModal } from './SignUpEmburseCards/SignUpConfirmationModal';
import { SignUpEmburseCards } from './SignUpEmburseCards/SignUpEmburseCards';
import { SignUpImplementationSelection } from './SignUpImplementationSelection/SignUpImplementationSelection';
import { SignUpPaymentSummary } from './SignUpPaymentSummary/SignUpPaymentSummary';
import { SignUpPersonalInfo } from './SignUpPersonalInfo/SignUpPersonalInfo';
import { SignUpPlanSelection } from './SignUpPlanSelection/SignUpPlanSelection';
import { SignUpReferralScreen } from './SignUpReferralScreen/SignUpReferralScreen';
import SignUpReviews from './SignUpReviews/SignUpReviews';
import { SignUpWelcomeScreen } from './SignUpWelcomeScreen/SignUpWelcomeScreen';
import { VerifyEmail } from './VerifyEmail';
import { STAGE, getAvailableStepsData } from './signUpWorkflowSteps';
import { getSignUpChannel, isAmexCompany, isWtiSignupCompany } from './singup_helper_functions';

import * as style from './SignUpWorkflow.styl';

const PENDO_MAPPING: Record<typeof STAGE[keyof typeof STAGE], typeof PendoEvents[keyof typeof PendoEvents]> = {
  [STAGE.EMAIL_VERIFICATION]: PendoEvents.SIGNUP_FLOW_EMAIL_VERIFICATION,
  [STAGE.COMPANY_INFORMATION]: PendoEvents.SIGNUP_FLOW_COMPANY_INFO,
  [STAGE.PERSONAL_INFORMATION]: PendoEvents.SIGNUP_FLOW_PERSONAL_INFO,
  [STAGE.PLAN_SELECTION]: PendoEvents.SIGNUP_FLOW_PLAN_SELECTION,
  [STAGE.IMPLEMENTATION_OPTIONS]: PendoEvents.SIGNUP_FLOW_IMPLEMENTATION_OPTIONS,
  [STAGE.EMBURSE_CARDS]: PendoEvents.SIGNUP_FLOW_EMBURSE_CARDS,
  [STAGE.BUSINESS_VERIFICATION]: PendoEvents.SIGNUP_FLOW_BUSINESS_VERIFICATION,
  [STAGE.REFERRAL_SCREEN]: PendoEvents.SIGNUP_FLOW_REFERRAL_SCREEN,
  [STAGE.RSM_PARTNER_REFERRAL_SCREEN]: PendoEvents.SIGNUP_FLOW_REFERRAL_SCREEN,
  [STAGE.SUBMIT_APPLICATION]: PendoEvents.SIGNUP_FLOW_SUBMIT_APPLICATION,
  [STAGE.PAYMENT_SUMMARY]: PendoEvents.SIGNUP_FLOW_PAYMENT_SUMMARY,
  [STAGE.FINISH]: PendoEvents.SIGNUP_FLOW_FINISHED
};

interface StagesMap {
  [key: string]: React.ComponentElement<any, any>;
}

const stages = Object.values(STAGE);

type INavDirection = 'next' | 'back' | 'skip';
type GatewayConnection = { verification_status?: string } | null;

function hasNoGatewayConnections(connections: GatewayConnection[]) {
  return connections.length === 0 || (connections.length === 1 && connections[0] === null);
}

function getIncompleteApplicationStage(isFromSales: boolean, isWtiPartner: boolean, cards: string) {
  if (isWtiPartner && cards === 'hide') {
    return STAGE.FINISH;
  }

  return isWtiPartner ? STAGE.EMBURSE_CARDS : STAGE.BUSINESS_VERIFICATION;
}

function getConnectionOutcome({
  connections,
  isFromSales,
  isWtiPartner,
  cards
}: {
  connections: GatewayConnection[];
  isFromSales: boolean;
  isWtiPartner: boolean;
  cards: string;
}) {
  if (hasNoGatewayConnections(connections)) {
    return {
      nextStage: STAGE.COMPANY_INFORMATION,
      replaceHistory: false,
      shouldApplyForEmburseCards: false
    };
  }

  const hasIncompleteCardsApplication = connections.some(
    (connection) => connection?.verification_status === 'unsubmitted' || connection?.verification_status === 'unverified'
  );
  const hasVerifiedCardsApplication = connections.some((connection) => connection?.verification_status === 'verified');

  if (hasIncompleteCardsApplication && !hasVerifiedCardsApplication) {
    return {
      nextStage: getIncompleteApplicationStage(isFromSales, isWtiPartner, cards),
      replaceHistory: true,
      shouldApplyForEmburseCards: false
    };
  }

  return {
    nextStage: isFromSales || isWtiPartner ? STAGE.FINISH : STAGE.PAYMENT_SUMMARY,
    replaceHistory: true,
    shouldApplyForEmburseCards: true
  };
}

const useSignupCompanyForm = (company: ICompanyModel) =>
  useForm<ICompanyForm>({
    defaultValues: {
      name: company.name === ' ' ? '' : company.name,
      street_address: company.street_address || '',
      address_city: company.address_city || '',
      postal_code: company.postal_code || '',
      website: company.website || '',
      address_state: {
        code: company.address_country_code || '',
        country_code: company.address_country_code || '',
        subdivision_name: company.address_state || ''
      },
      address_country_code: company.address_country_code || 'USA',
      phone_number: company.phone_number || '',
      employees_with_expenses_range: (company as any).employees_with_expenses_range || ''
    }
  });

function getCardsOffer(company: ICompanyModel, isAdditionalCompany?: boolean) {
  if (!company) {
    return 'offer';
  }

  if (isAdditionalCompany || isAmexCompany(company) || isWtiSignupCompany(company)) {
    return 'hide';
  }

  const utm_parameter = company?.utm_parameter as any;

  return utm_parameter?.referralParameters?.cards || '';
}

export const SignUpWorkflow: React.FC<{ startStage?: string; isAdditionalCompany?: boolean }> = ({ startStage, isAdditionalCompany }) => {
  const { acsUrl, assertionResponse } = window as any;
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = React.useState(0);
  const [currentStage, setCurrentStage] = React.useState<string>(STAGE.COMPANY_INFORMATION);
  const [completed, setCompleted] = React.useState<{ [k: number]: boolean }>({});
  const [applyForEmburseCards, setApplyForEmburseCards] = React.useState(false);
  const [apiError, setApiError] = React.useState<React.ReactNode>(null);
  const company = useSelector((state: IAppStateContainer) => state.company.company) as any;
  const user = useSelector((state: IAppStateContainer) => state.currentUser.user);
  const channel = getSignUpChannel(company);
  const cards = getCardsOffer(company, isAdditionalCompany);
  const isFromSales = channel === 'sales';
  const isRsmPartner = [PARTNER_ID_RSM_FAO, PARTNER_ID_RSM_VAR].includes(channel);
  const isWtiPartner = isWtiSignupCompany(company);
  let workflowPartnerId: string | undefined;
  if (isWtiPartner) {
    workflowPartnerId = PARTNER_ID_WTI_GLOBAL;
  } else if (isRsmPartner) {
    workflowPartnerId = channel;
  }
  const logo = getPartnerAppLogoPath({
    isWtiBranding: isWtiPartner,
    defaultLogoPath: window.whitelabelConfiguration?.tags?.appLogoPath
  });
  const logoStyle = getPartnerSignupLogoStyle({ isWtiBranding: isWtiPartner });
  const logoWrapperStyle = isWtiPartner ? { marginBottom: 12 } : undefined;
  const skipReferralCards =
    isAdditionalCompany || isAmexCompany(company) ? false : isFromSales && !(cards === 'offer' || cards === 'force');
  const { steps, stageToStepMapping } = React.useMemo(
    () => getAvailableStepsData(isFromSales, cards, skipReferralCards, isAdditionalCompany, workflowPartnerId),
    [isFromSales, cards, skipReferralCards, isAdditionalCompany, workflowPartnerId]
  );

  useTimeoutManager();

  const companyFormMethods = useSignupCompanyForm(company);

  const setGenericStripeError = () => {
    setApiError('There was an issue contacting our payment processor — please try again later');
  };

  const handleConnection = async () => {
    if (!company?.id) {
      return;
    }

    const connections = (await CompanyApi.getCompanyGatewayConnections(company.id)) as GatewayConnection[];
    const { nextStage, replaceHistory, shouldApplyForEmburseCards } = getConnectionOutcome({
      connections,
      isFromSales,
      isWtiPartner,
      cards
    });

    if (shouldApplyForEmburseCards) {
      setApplyForEmburseCards(true);
    }

    goToStage(nextStage, replaceHistory);
  };

  React.useEffect(() => {
    const isVerified = user.state !== 'unverified';
    const hasInfoFilled = user.has_info_filled;
    if (!isVerified) {
      goToStage(STAGE.EMAIL_VERIFICATION);
    }
    if (hasInfoFilled) {
      handleConnection().catch(() => goToStage(STAGE.COMPANY_INFORMATION));
    }
  }, [user.state]);

  React.useEffect(() => {
    const effectiveStartStage = startStage === STAGE.PAYMENT_SUMMARY && (isFromSales || isWtiPartner) ? STAGE.FINISH : startStage;
    let index = stages.indexOf(effectiveStartStage as STAGE);
    if (index !== -1) {
      goToStage(stages[index], true);
    }
  }, [isFromSales, isWtiPartner, startStage]);

  function getNextStage(direction: INavDirection) {
    let index = stages.indexOf(currentStage as STAGE);
    if (index === -1) {
      return undefined;
    }
    const delta = direction === 'next' ? 1 : -1;
    while (index >= 0 && index < stages.length) {
      index = index + delta;
      const nextStage = stages[index];

      if (stageToStepMapping[nextStage] !== undefined) {
        if (delta === -1 && nextStage === STAGE.SUBMIT_APPLICATION) {
          // we want to return to the start of the cards application
          return STAGE.EMBURSE_CARDS;
        }
        return nextStage;
      }
    }
    return undefined;
  }

  function getFirstStageOfNextStep() {
    let index = activeStep;
    while (index >= 0 && index < steps.length) {
      index = index + 1;
      const newStep = steps[index];
      const nextStage = newStep?.stages?.[0];
      if (nextStage !== undefined) {
        return nextStage;
      }
    }
    return undefined;
  }

  function goNext() {
    continueFlow('next');
  }

  function goBack() {
    continueFlow('back');
  }

  function goSkip() {
    continueFlow('skip');
  }

  function continueFlow(direction: INavDirection = 'next') {
    let newStage;
    if (direction === 'skip') {
      newStage = getFirstStageOfNextStep();
    } else {
      newStage = getNextStage(direction);
    }
    if (newStage === undefined) {
      newStage = STAGE.FINISH;
    }
    const pendoEvent = PENDO_MAPPING[newStage];
    if (pendoEvent) {
      PendoService.trackEvent(pendoEvent);
    }
    goToStage(newStage, direction === 'next');
  }

  function goToStage(stage: STAGE, completedVal: boolean = false) {
    const nextStep = stageToStepMapping[stage];

    // we are changing steps, we need to make the correct updates to completed and activeStep
    if (nextStep !== activeStep) {
      let start = Math.min(activeStep, nextStep);
      let end = Math.max(activeStep, nextStep);
      // we need to set all completed steps between the current and next step to false
      const newCompleted: Record<number, boolean> = {};
      for (let i = start; i < end; i++) {
        newCompleted[i] = completedVal;
      }

      // it's a representative and we are going to the final step (it's implied that we are skipping cards)
      if (isFromSales && nextStep === steps.length - 1 && skipReferralCards) {
        const cardsIndex = steps.findIndex((step) => step.id === STAGE.EMBURSE_CARDS);
        if (cardsIndex) {
          newCompleted[cardsIndex] = false;
        }
      }
      setCompleted({ ...completed, ...newCompleted });
      setActiveStep(nextStep);
    }
    if (stage === STAGE.FINISH) {
      setCompleted({ ...completed, [activeStep]: true });
    }
    setCurrentStage(stage);
  }

  const onUserConfirmation = async () => {
    await signupForEmburseCards(company, user);
    goNext();
  };

  const onEmburseCardSelection = async () => {
    return dispatch(
      new ShowModal(SignUpConfirmationModal, {
        user: user,
        company,
        onConfirm: () => onUserConfirmation()
      })
    );
  };

  async function activateCompany() {
    // For Stripe flows, company activation happens immediately after payment in SignUpPaymentSummary
    // For non-Stripe flows (sales leads, RSM partners, external billing), we still need to activate here
    try {
      // Check if company is already active (activated during payment)
      if (company.state !== 'active') {
        await CompanyApi.activateCompany(company.id);
      }
      GoogleAnalyticsService.trackEvent(GoogleEvents.CONVERSION, { send_to: 'AW-934154673/YhM_CMrbxKUaELGjuL0D' });
      window.location.href = '/';
    } catch (error) {
      toast.error('Error activating company', error);
    }
  }

  function referralScreen() {
    return (
      <SignUpReferralScreen
        nextStep={goNext}
        activateCompany={activateCompany}
        company={company}
        applyForEmburseCards={applyForEmburseCards}
        onBack={() => goToStage(STAGE.PERSONAL_INFORMATION)}
        isRsmPartner={isRsmPartner}
      />
    );
  }

  // Map of stages to respective components. The next and previous stages are the functions above to navigate between stages
  const STAGES_MAP: StagesMap = {
    [STAGE.EMAIL_VERIFICATION]: <VerifyEmail />,
    [STAGE.COMPANY_INFORMATION]: (
      <FormProvider {...companyFormMethods}>
        <CompanyInformationForm company={company} isAdditionalCompany={isAdditionalCompany} onCompanyInfoSubmitNewSignup={goNext} />
      </FormProvider>
    ),
    [STAGE.PERSONAL_INFORMATION]: <SignUpPersonalInfo onNext={goNext} onBack={goBack} showFlexSpacing />,
    [STAGE.PLAN_SELECTION]: (
      <SignUpPlanSelection onPlanSelection={goNext} onBack={goBack} channel={channel} onError={setGenericStripeError} />
    ),
    [STAGE.IMPLEMENTATION_OPTIONS]: (
      <SignUpImplementationSelection onImplementationSelection={goNext} onBack={goBack} onError={setGenericStripeError} />
    ),
    [STAGE.EMBURSE_CARDS]: <SignUpEmburseCards onEmburseCardSelection={onEmburseCardSelection} onBack={goBack} onSkip={goSkip} />,
    [STAGE.BUSINESS_VERIFICATION]: (
      <GoToEmburse target="_self" path="/external-accounts" label="" acsUrl={acsUrl} samlResponse={assertionResponse} autoClick />
    ),
    [STAGE.REFERRAL_SCREEN]: referralScreen(),
    [STAGE.RSM_PARTNER_REFERRAL_SCREEN]: referralScreen(),
    [STAGE.PAYMENT_SUMMARY]: (
      <SignUpPaymentSummary
        company={company}
        emburseCards={applyForEmburseCards}
        onSummaryConfirmation={goNext}
        onError={setGenericStripeError}
      />
    ),
    [STAGE.FINISH]: <SignUpWelcomeScreen activateCompany={activateCompany} />
  };

  let leftContent = null;
  const shouldTopAlignWorkflowContent = currentStage !== STAGE.EMAIL_VERIFICATION;

  if (currentStage === STAGE.EMAIL_VERIFICATION) {
    leftContent = (
      <Grid item justifyContent="center" className="reviewsLeft">
        <Grid item container style={{ height: '100%' }} alignContent="center">
          <SignUpReviews />
        </Grid>
      </Grid>
    );
  } else if (![STAGE.PAYMENT_SUMMARY, STAGE.FINISH].includes(currentStage as STAGE)) {
    leftContent = (
      <Grid item justifyContent="center" className="reviewsLeft">
        <div style={{ height: '100%', alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
          <div style={{ alignContent: 'flex-start', marginTop: 106, minWidth: 250 }}>
            <Typography variant="h6">Getting Started</Typography>
            <Spacer size={30} />
            <Stepper orientation="vertical" activeStep={activeStep}>
              {steps.map((step, index) => {
                const labelProps: {
                  optional?: React.ReactNode;
                } = {};
                if (!step.label || !step.content) {
                  return null;
                }

                if (completed[index] === false && index < activeStep) {
                  labelProps.optional = (
                    <Typography variant="caption" className="skipped">
                      Skipped
                    </Typography>
                  );
                } else if (step.id === STAGE.EMBURSE_CARDS) {
                  labelProps.optional = (
                    <Typography variant="caption" className="skipped">
                      {skipReferralCards ? 'Skipped' : 'Optional'}
                    </Typography>
                  );
                }
                return (
                  <Step key={index} expanded completed={completed[index]}>
                    <StepLabel {...labelProps}>{step.label}</StepLabel>
                    <StepContent>
                      {step.content.map((content, stepIndex) => (
                        <Typography style={{ fontSize: '12px' }} key={stepIndex}>
                          {content}
                        </Typography>
                      ))}
                    </StepContent>
                  </Step>
                );
              })}
            </Stepper>
          </div>
        </div>
      </Grid>
    );
  }

  return (
    <div className={style.global} style={{ height: '100%' }}>
      <Grid container alignItems="stretch" justifyContent="center" style={{ height: '100%' }} wrap="nowrap">
        {leftContent}
        <Grid container item className="signUpRight" xs>
          <Grid
            container
            alignItems="center"
            wrap="nowrap"
            justifyContent={shouldTopAlignWorkflowContent ? 'flex-start' : 'center'}
            direction="column"
            sx={{ height: '100%', overflowY: 'auto', paddingTop: shouldTopAlignWorkflowContent ? 3 : 0 }}
          >
            <Grid item className="maxWidth centerText" style={logoWrapperStyle}>
              <img className="logo" src={logo} alt="logo" style={logoStyle} />
            </Grid>
            {apiError && (
              /* 0 height to not shift page content */ <div style={{ height: 0, overflow: 'visible' }}>
                <Spacer size={32} />
                <Alert severity="error" onClose={() => setApiError(null)}>
                  {apiError}
                </Alert>
              </div>
            )}
            <Grid item sx={{ height: '100%' }}>
              {STAGES_MAP[currentStage]}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};
